import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export const useUserFollow = (userId: string, currentUserId?: string) => {
  const queryClient = useQueryClient();

  // Check if current user is following this user
  const { data: isFollowing = false } = useQuery({
    queryKey: ['user-follow', currentUserId, userId],
    queryFn: async () => {
      if (!currentUserId || currentUserId === userId) return false;

      const { data, error } = await supabase
        .from('user_follows')
        .select('id')
        .eq('follower_id', currentUserId)
        .eq('following_id', userId)
        .maybeSingle();

      if (error) {
        console.error('Error checking follow status:', error);
        return false;
      }

      return !!data;
    },
    enabled: !!currentUserId && currentUserId !== userId,
  });

  // Get follower count
  const { data: followerCount = 0 } = useQuery({
    queryKey: ['follower-count', userId],
    queryFn: async () => {
      const { count, error } = await supabase
        .from('user_follows')
        .select('*', { count: 'exact', head: true })
        .eq('following_id', userId);

      if (error) {
        console.error('Error getting follower count:', error);
        return 0;
      }

      return count || 0;
    },
  });

  // Get following count
  const { data: followingCount = 0 } = useQuery({
    queryKey: ['following-count', userId],
    queryFn: async () => {
      const { count, error } = await supabase
        .from('user_follows')
        .select('*', { count: 'exact', head: true })
        .eq('follower_id', userId);

      if (error) {
        console.error('Error getting following count:', error);
        return 0;
      }

      return count || 0;
    },
  });

  // Follow mutation
  const followMutation = useMutation({
    mutationFn: async () => {
      if (!currentUserId) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('user_follows')
        .insert({
          follower_id: currentUserId,
          following_id: userId,
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-follow', currentUserId, userId] });
      queryClient.invalidateQueries({ queryKey: ['follower-count', userId] });
      queryClient.invalidateQueries({ queryKey: ['following-count', currentUserId] });
      queryClient.invalidateQueries({ queryKey: ['activity-feed'] });
      toast.success('Successfully followed user');
    },
    onError: (error) => {
      console.error('Error following user:', error);
      toast.error('Failed to follow user');
    },
  });

  // Unfollow mutation
  const unfollowMutation = useMutation({
    mutationFn: async () => {
      if (!currentUserId) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('user_follows')
        .delete()
        .eq('follower_id', currentUserId)
        .eq('following_id', userId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-follow', currentUserId, userId] });
      queryClient.invalidateQueries({ queryKey: ['follower-count', userId] });
      queryClient.invalidateQueries({ queryKey: ['following-count', currentUserId] });
      queryClient.invalidateQueries({ queryKey: ['activity-feed'] });
      toast.success('Successfully unfollowed user');
    },
    onError: (error) => {
      console.error('Error unfollowing user:', error);
      toast.error('Failed to unfollow user');
    },
  });

  return {
    isFollowing,
    followerCount,
    followingCount,
    follow: followMutation.mutate,
    unfollow: unfollowMutation.mutate,
    isLoading: followMutation.isPending || unfollowMutation.isPending,
  };
};
